import {_} from 'lodash';
import * as ko from "knockout";
import {viewModel as SystemAccessDetails} from "./details";
import {IRouteDefinitionParams, IRuleset, ISystem, ISystemRole} from "../../index";
import PromiseRequest from "../../req/PromiseRequest";
import {ObjectTypeId, Permission, SYSTEM_MEGA_EADM} from "../../constants";
import {createErrorMessage} from "../../util/misc";
import {IIsSystemPropertyMutable, ISystemMutable, ISystemRoleMutable, TErrorManagers} from "./index";
import {getObjectFromRouteData} from "../../util/request";
import {ErrorObject} from "../../error/ErrorObject";
import {getCancelButton, getDiscardButton, getSubmitButton} from "../../constants/RouteDefinitions";
import {nsSystemAccess} from "./nsSystemAccess";
import mutableHtml from './mutable.html?raw';

export const template = mutableHtml

export abstract class viewModel extends SystemAccessDetails {

    protected isPropertyMutable: IIsSystemPropertyMutable = nsSystemAccess.getIsPropertyMutable()

    protected abstract reqSubmit: PromiseRequest;

    protected md: ISystemMutable = nsSystemAccess.createSystemMutable()

    private errorManagers: TErrorManagers;
    private readonly ObjectTypeId = ObjectTypeId;

    protected abstract onSubmit(): void;

    constructor(params: IRouteDefinitionParams) {
        super(params);

        this.kosub.subscribe([this.md.multiplerolesamecompany, this.md.multiplerolesamedepartment], () => {
            if (this.md.multiplerolesamecompany() || this.md.multiplerolesamedepartment()) {
                this.md.prioritybasedroles(false)
            }
        })

        this.errorManagers = {
            systemaccessinfo: this.__tabManager!.getTab('systemaccessinfo')!.errorManager,
            systemaccessroles: this.__tabManager!.getTab('systemaccessroles')!.errorManager,
        }

        this.errorManagers.systemaccessinfo.addErrorObject(this.md.errName)
        this.errorManagers.systemaccessinfo.addErrorObject(this.md.errDescription)
        this.errorManagers.systemaccessinfo.addErrorObject(this.md.errSystemOwner)
    }

    protected override initButtons() {
        const cancelButton = getCancelButton();
        if (cancelButton) {
            this.pageButtons.add(cancelButton);
        }
        this.pageButtons.add(getDiscardButton({
            enabled: ko.pureComputed(() => !!(this.isReady() && this.isChanged())),
            onClick: this.onDiscardChangesClick.bind(this),
        }))
        this.pageButtons.add(getSubmitButton({
            onClick: this.onSubmit.bind(this),
            enabled: ko.pureComputed(() => !!(this.isReady() && this.isChanged() && this.isValid())),
            permissionLevel: Permission.SuperEmployee,
        }))
    }

    protected override onGetSuccess(data: ISystem[]) {
        // populate static data
        this.d = getObjectFromRouteData(data, this.__routeDef, this.__routeParams);

        if (!this.d) {
            this.messageData(createErrorMessage(`no data`));
            return;
        }

        if (this.d.isreadonly || this.d.definitionreadonly) {
            this.messageData(createErrorMessage(`can not be changed`));
            return;
        }

        // populate mutable data
        // Combobox v2 labels any non-null `selected` immediately (before list match).
        // An id-only stub with id 0 / missing name becomes "[object Object]" in the input —
        // seed null when there is no real ruleset, else `{id}` for initialMatch by id.
        this.md.availableto(this.d.availableto_id ? {id: this.d.availableto_id} as IRuleset : null);
        this.md.managedby(this.d.managedby_id ? {id: this.d.managedby_id} as IRuleset : null);
        this.md.name(this.d.name);
        this.md.description(this.d.description);
        this.md.multiplerolesamecompany(this.d.multiplerolesamecompany);
        this.md.multiplerolesamedepartment(this.d.multiplerolesamedepartment);
        this.md.prioritybasedroles(this.d.prioritybasedroles)
        this.md.purgemanualdepartmentpermissions(this.d.purgemanualdepartmentpermissions)
        this.md.systemowner(this.d.systemowner);
        this.md.roles(
            _(this.d.roles)
                .sortBy('roleenum')
                .map((r: ISystemRole) => {
                    const rolememberrulesets = r.rolememberrulesets?.split(',').filter(Boolean).map((o: string) => ko.observable(parseInt(o)))
                    const availabletorulesets = (r.availabletorulesets ?? '').split(',').filter(Boolean).map((o: string) => ko.observable(parseInt(o)))
                    const memberofgroups = r.memberofgroups.split(',').filter(Boolean).map((o: string) => ko.observable(parseInt(o)))
                    const ret: ISystemRoleMutable = {
                        id: r.id,
                        $original: r,
                        availableto_id: ko.observable(r.availableto_id),
                        availabletorulesets: ko.observableArray(availabletorulesets),
                        description: ko.observable(r.description),
                        memberofgroup: ko.observable(r.memberofgroup),
                        memberofgroups: ko.observableArray(memberofgroups),
                        metadata: ko.observable(r.metadata),
                        name: ko.observable(r.name),
                        priority: ko.observable(r.priority),
                        purgemanualdepartmentpermissions: ko.observable(r.purgemanualdepartmentpermissions),
                        recursive: ko.observable(r.recursive),
                        roleenum: ko.observable(r.roleenum),
                        rolememberruleset: ko.observable(r.rolememberruleset),
                        rolememberrulesets: ko.observableArray(rolememberrulesets),
                        systemowner: ko.observable(r.systemowner),
                        errName: new ErrorObject({name: 'name'}),
                        errRoleEnum: new ErrorObject({name: 'roleEnum'}),
                        errSystemOwner: new ErrorObject({name: 'systemOwner'}),
                        errPriority: new ErrorObject({name: 'priority'}),
                    }
                    return ret
                })
                .value()
        );

        this.md.isSystemMegaEadm = this.md.name() == SYSTEM_MEGA_EADM

        if (this.md.isSystemMegaEadm) {
            let key: keyof IIsSystemPropertyMutable
            for (key in this.isPropertyMutable) {
                this.isPropertyMutable[key] = false
            }
            this.isPropertyMutable.availableto_id = true
        }

        this.messageData(null);
        this.isReady(true);
    }

    protected isValid() {
        return this.__tabManager!.validate() ?? false;
    }

    protected isChanged(): boolean {
        const a = this.md.multiplerolesamecompany() != this.d.multiplerolesamecompany;
        const b = this.md.multiplerolesamedepartment() != this.d.multiplerolesamedepartment;
        const j = this.md.prioritybasedroles() != this.d.prioritybasedroles
        const k = this.md.purgemanualdepartmentpermissions() != this.d.purgemanualdepartmentpermissions
        const c = (this.md.availableto()?.id ?? 0) != this.d.availableto_id;
        const d = (this.md.managedby()?.id ?? 0) != this.d.managedby_id;
        const e = this.md.name() != this.d.name;
        const f = !!this.getCreatedRoles().length;
        const g = !!this.getUpdatedRoles().length;
        const h = !!this.getDeletedRoles().length;
        const i = this.md.description() != this.d.description;
        const l = this.md.systemowner() != this.d.systemowner;
        return a || b || c || d || e || f || g || h || i || j || k || l
    }

    onDiscardChangesClick() {
        this.errorManagers.systemaccessinfo.discard();
        this.errorManagers.systemaccessroles.discard();
        this.init();
    }

    protected getSubmitData(): ISystem {
        return nsSystemAccess.systemMutable2Static(this.md)
    }

    protected getCreatedRoles(): ISystemRole[] {
        return this.md.roles()
            .filter(r => r.id === 0)
            .map(r => this.roleMutable2Static(r));
    }

    protected getUpdatedRoles(): ISystemRole[] {
        return this.md.roles()
            .filter(r => !!r.id)
            .map(r => this.roleMutable2Static(r))
            .filter((newR: ISystemRole) => {
                const oldR = _.clone(this.d.roles.find(r => r.id == newR.id))
                // For some reason the API produses a stray ending comma to the meberofgroups list.
                // Let's remove it before comparing.
                if (oldR.memberofgroups.endsWith(',')) {
                    oldR.memberofgroups = oldR.memberofgroups.split(',').filter(Boolean).join(',')
                }
                // The editor keeps ruleset lists in name order (and the API adds a trailing comma to
                // rolememberrulesets); compare the set, not the order.
                oldR.rolememberrulesets = _.sortBy(_.uniq((oldR.rolememberrulesets ?? '').split(',').filter(Boolean).map(Number))).join(',')
                oldR.availabletorulesets = _.sortBy(_.uniq((oldR.availabletorulesets ?? '').split(',').filter(Boolean).map(Number))).join(',')
                return !_.isEqual(oldR, newR);
            });
    }

    protected getDeletedRoles(): ISystemRole[] {
        return this.d.roles
            .filter(oldR =>
                !this.md.roles().find(newR => newR.id == oldR.id)
            );
    }

    private roleMutable2Static(r: ISystemRoleMutable): ISystemRole {
        const memberofgroups = r.memberofgroups().map(o => o()).filter(Boolean).join(',')
        const rolememberrulesets = _.sortBy(_.uniq(r.rolememberrulesets().map(o => o()).filter(Boolean))).join(',')
        const availabletorulesets = _.sortBy(_.uniq(r.availabletorulesets().map(o => o()).filter(Boolean))).join(',')
        const systemrole: Partial<ISystemRole> = {
            availableto_id: r.availableto_id(),
            availabletorulesets: availabletorulesets,
            description: r.description(),
            id: r.id,
            memberofgroup: r.memberofgroup(),
            memberofgroups: memberofgroups,
            metadata: r.metadata(),
            name: r.name(),
            overrides: [],
            priority: r.priority(),
            purgemanualdepartmentpermissions: r.purgemanualdepartmentpermissions(),
            recursive: r.recursive(),
            roleenum: Number(r.roleenum()),
            rolememberruleset: r.rolememberruleset(),
            rolememberrulesets: rolememberrulesets,
            systemid: this.d.id,
            systemowner: r.systemowner(),
        };
        return _.defaults(systemrole, r.$original)
    }

    override dispose() {
        super.dispose();
        this.reqSubmit.abort();
    }
}
